// useSidebarSelection.js

import { useCallback, useMemo } from 'react';

// Context
import { useDatabase } from 'context/DatabaseContext';
import { useNavigation } from 'context/NavigationContext';

// Routing
import { useLocation } from 'react-router-dom';

// Utilities
import { SectionType } from 'utilities/Enums';
import { buildBaseMemberProfilePath } from 'utilities/Routing';
import {
    getAppearAsKeyFromSearch,
    isCollectionRoutePath,
} from 'utilities/collectionPaths';

function getRouteResourceKey(pathname, basePath) {
    if (!pathname || !basePath) return '';

    const prefix = `${basePath}/`;
    if (pathname.startsWith(prefix)) {
        const remainder = pathname.slice(prefix.length);
        const segment = remainder.split('/')[0] || '';
        try {
            return decodeURIComponent(segment).trim();
        } catch {
            return segment.trim();
        }
    }

    // Visitor Main routes: /s/:base/:space/channels|:collection/:id
    const visitorMatch = pathname.match(
        new RegExp(`^/s/[^/]+/[^/]+${basePath}/([^/]+)`),
    );
    if (visitorMatch?.[1]) {
        try {
            return decodeURIComponent(visitorMatch[1]).trim();
        } catch {
            return String(visitorMatch[1] || '').trim();
        }
    }

    return '';
}

/**
 * useSidebarSelection
 *
 * Derives which sidebar rows are active from route, section, and database context.
 *
 * @param {object} params - Hook parameters.
 * @param {object|null} params.user - Current signed-in user.
 * @param {string} params.activeBaseKey - Active space key.
 *
 * @returns {object} Sidebar selection helpers.
 */
export default function useSidebarSelection({ user, activeBaseKey }) {
    const location = useLocation();
    const { currentSection } = useNavigation();
    const { activeDatabase } = useDatabase();

    const currentUserKey = useMemo(() => (
        String(user?.key || user?.userKey || '').trim()
    ), [user?.key, user?.userKey]);

    const ownProfilePath = useMemo(() => {
        if (!activeBaseKey || !currentUserKey) return '';
        return buildBaseMemberProfilePath(activeBaseKey, currentUserKey);
    }, [activeBaseKey, currentUserKey]);

    const activeDatabaseKeyFromPath = useMemo(() => (
        getRouteResourceKey(location.pathname, '/collection')
        || getRouteResourceKey(location.pathname, '/database')
    ), [location.pathname]);

    const activeDashboardKeyFromPath = useMemo(() => (
        getRouteResourceKey(location.pathname, '/dashboard')
    ), [location.pathname]);

    const activeChannelKeyFromPath = useMemo(() => (
        getRouteResourceKey(location.pathname, '/channels')
    ), [location.pathname]);

    const isOwnProfileSelected = useMemo(() => {
        if (!ownProfilePath) return false;
        return location.pathname === ownProfilePath
            || location.pathname.startsWith(`${ownProfilePath}/`);
    }, [location.pathname, ownProfilePath]);

    const isSectionSelected = useCallback((section) => {
        if (section === SectionType.USER) {
            return isOwnProfileSelected;
        }

        return currentSection === section;
    }, [currentSection, isOwnProfileSelected]);

    const activeAppearAsKey = useMemo(() => (
        getAppearAsKeyFromSearch(location.search)
    ), [location.search]);

    const isDatabaseSelected = useCallback((databaseKey, appearAsKey = '') => {
        const normalizedKey = String(databaseKey || '').trim();
        if (!normalizedKey) return false;

        const wantedAppearAs = String(appearAsKey || '').trim().toLowerCase();
        if (wantedAppearAs !== activeAppearAsKey) return false;

        const pathMatches = activeDatabaseKeyFromPath === normalizedKey;
        const activeDatabaseKey = String(activeDatabase?.key || '').trim();
        const contextMatches = activeDatabaseKey === normalizedKey;
        if (!pathMatches && !contextMatches) return false;

        if (pathMatches) return true;

        const isDatabaseRoute = isCollectionRoutePath(location.pathname);
        const isNodeRoute = location.pathname.startsWith('/node/');

        return isDatabaseRoute || isNodeRoute;
    }, [
        activeAppearAsKey,
        activeDatabase?.key,
        activeDatabaseKeyFromPath,
        location.pathname,
    ]);

    const isDashboardSelected = useCallback((dashboardKey) => {
        const normalizedKey = String(dashboardKey || '').trim();
        if (!normalizedKey) return false;

        return activeDashboardKeyFromPath === normalizedKey;
    }, [activeDashboardKeyFromPath]);

    const isChannelSelected = useCallback((channelKey) => {
        const normalizedKey = String(channelKey || '').trim();
        if (!normalizedKey) return false;

        return activeChannelKeyFromPath === normalizedKey;
    }, [activeChannelKeyFromPath]);

    return {
        isSectionSelected,
        isDatabaseSelected,
        isDashboardSelected,
        isChannelSelected,
        isOwnProfileSelected,
    };
}
